import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MessageCircle,
  Sparkles,
  Network,
  Users,
  HeartHandshake,
  CalendarHeart,
  UserCircle,
  ArrowRight,
  CalendarDays,
  Compass,
  type LucideIcon,
} from "lucide-react";
import { Conversation } from "../anchors/conversation";
import { People } from "../anchors/people/People";
import { Relationships } from "../anchors/relationships/Relationships";
import { KnowledgeGraph } from "../anchors/knowledge-graph";
import { Weave } from "../anchors/weave/Weave";
import { Dates } from "../anchors/dates/Dates";
import { AboutYou } from "../anchors/about-you/AboutYou";
import { getCsrfToken } from "../lib/auth";
import { TeachingBanner } from "../components/TeachingBanner";
import { L2RailShell, type L2NavItem } from "../components/L2Nav";
import { Breadcrumb } from "../components/Breadcrumb";

/** Per-lens teaching banner id. Each lens has its own short blurb; the
 *  catch-all `explore_overview` covers the hub. Keyed by the PIVOTS id. */
const LENS_TEACHING_ID: Record<string, string> = {
  conversation: "recall_page_overview",
  weave: "weave_overview",
  graph: "knowledge_graph_overview",
  "about-you": "about_you_overview",
  people: "people_overview",
  relationships: "relationships_overview",
  dates: "dates_overview",
};

interface Pivot {
  id: string;
  /** URL-friendly slug. The route accepts /explore?lens=<slug>. */
  slug: string;
  /** Aliases the URL parser also accepts so older links + slug
   *  variants the user might type still resolve. */
  aliases: string[];
  label: string;
  Icon: LucideIcon;
  hint: string;
  /** Fuller copy for the mobile hub-card body. */
  blurb: string;
  /** Tint for the gradient icon tile. */
  tint: "violet" | "amber" | "sky" | "emerald" | "pink" | "slate";
  ready: boolean;
  /** If set, picking this rail item navigates cross-section rather
   *  than rendering inline content. Used for Agenda which has its own
   *  top-level route + page chrome. */
  externalTo?: string;
}

const PIVOTS: Pivot[] = [
  {
    id: "conversation",
    slug: "recall",
    aliases: ["conversation"],
    label: "Recall",
    Icon: MessageCircle,
    hint: "Ask Memso to recall, weave, synthesize",
    blurb: "Hybrid retrieval across captures, conversations, calendar, and threads. Reply with cited sources.",
    tint: "violet",
    ready: true,
  },
  {
    id: "weave",
    slug: "weave",
    aliases: ["synthesis", "briefs"],
    label: "Weave",
    Icon: Sparkles,
    hint: "What Memso synthesized",
    blurb: "Snapshot briefs Memso weaves across your people, engagements, and week. Refresh to re-synthesize.",
    tint: "violet",
    ready: true,
  },
  {
    id: "graph",
    slug: "knowledge-graph",
    aliases: ["graph", "knowledge_graph"],
    label: "Knowledge graph",
    Icon: Network,
    hint: "See connections",
    blurb: "React-flow canvas over the people, engagements, and insights Memso connected, with depth + type filters.",
    tint: "sky",
    ready: true,
  },
  {
    id: "about-you",
    slug: "about-you",
    aliases: ["profile", "you", "aboutyou"],
    label: "About you",
    Icon: UserCircle,
    hint: "What Memso has learned about you",
    blurb: "Behavior facets Memso inferred from your captures and threads. Brands, spend, travel, diet, rhythms. Tentative, with a feedback loop.",
    tint: "amber",
    ready: true,
  },
  {
    id: "people",
    slug: "people",
    aliases: [],
    label: "People",
    Icon: Users,
    hint: "Who came up when",
    blurb: "Directory of everyone Memso has met across your captures, emails, and meetings. Sort by recent / alpha / frequency.",
    tint: "emerald",
    ready: true,
  },
  {
    id: "relationships",
    slug: "relationships",
    aliases: [],
    label: "Relationships",
    Icon: HeartHandshake,
    hint: "Tag who matters",
    blurb: "Tag the people who matter so Memso can synthesize across them. Family, colleagues, vendors, friends.",
    tint: "pink",
    ready: true,
  },
  {
    id: "dates",
    slug: "dates",
    aliases: ["important-dates", "important_dates"],
    label: "Dates",
    Icon: CalendarHeart,
    hint: "Birthdays, anniversaries, events",
    blurb: "Every date Memso has woven from your signals. Birthdays, anniversaries, recurring and one-off events. Confirm what is inferred.",
    tint: "pink",
    ready: true,
  },
  {
    id: "agenda",
    slug: "agenda",
    aliases: [],
    label: "Agenda",
    Icon: CalendarDays,
    hint: "What is coming up",
    blurb: "Meetings, trips and dated milestones grouped by kind. Opens the dedicated Agenda surface.",
    tint: "sky",
    ready: true,
    externalTo: "/agenda",
  },
];

const LAST_LENS_KEY = "last_active_lens";

/** Resolve a slug or id (or alias) from a URL param to a pivot id.
 *  Tolerates case + the legacy id-based URL shape so old bookmarks
 *  still work. */
function resolveLensParam(raw: string | null): string | null {
  if (!raw) return null;
  const norm = raw.toLowerCase().trim();
  for (const p of PIVOTS) {
    if (p.id === norm) return p.id;
    if (p.slug === norm) return p.id;
    if (p.aliases.includes(norm)) return p.id;
  }
  return null;
}

async function persistLastLens(lensId: string): Promise<void> {
  try {
    const csrf = getCsrfToken();
    await fetch(`/api/user/settings`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ key: LAST_LENS_KEY, value: lensId }),
    });
  } catch {
    /* best-effort */
  }
}

export function Explore() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lens: pathLens } = useParams<{ lens?: string }>();
  // Lens resolution priority on first paint:
  //   1. /explore/:lens path param (canonical from polish-31; back
  //      gesture + share-links use this shape)
  //   2. ?lens=<slug>  (legacy from polish-28 + Today snapshot
  //      cards; still resolves so older bookmarks work)
  //   3. null  -> Explore Home (the hub tiles are the landing spot,
  //      with the synthetic "Explore Home" rail item selected). We do
  //      NOT auto-restore the last lens so /explore is a stable home.
  const [active, setActive] = useState<string | null>(() => {
    if (pathLens) {
      const fromPath = resolveLensParam(pathLens);
      if (fromPath) return fromPath;
    }
    const params = new URLSearchParams(location.search);
    return resolveLensParam(params.get("lens"));
  });
  const [savedLensReady, setSavedLensReady] = useState(false);

  // Re-resolve when the URL changes (browser back, deep link from
  // Today snapshot card, etc.). Path param wins; legacy query
  // string still resolves for old bookmarks. Bare /explore lands on
  // Explore Home (no lens), the hub tiles.
  useEffect(() => {
    const fromPath = pathLens ? resolveLensParam(pathLens) : null;
    const params = new URLSearchParams(location.search);
    const fromQuery = resolveLensParam(params.get("lens"));
    const fromUrl = fromPath ?? fromQuery;
    if (fromUrl) {
      setActive(fromUrl);
      void persistLastLens(fromUrl);
      setSavedLensReady(true);
      return;
    }
    // No URL lens: this is Explore Home. Reset any in-memory lens so
    // the URL + UI agree (e.g. the user dropped to the hub via the
    // rail's "Explore Home" item or the in-app back chevron).
    setActive(null);
    setSavedLensReady(true);
    // active intentionally omitted: the URL drives state, the
    // setter writes are the only legitimate state-update path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathLens, location.search]);

  const base = location.pathname.startsWith("/u/")
    ? location.pathname.replace(/\/explore(\/.*)?$/, "/explore")
    : "/explore";

  // Drill-in: PUSH a /explore/<slug> entry so the system back
  // gesture (Android Chrome PWA edge-swipe + system back button)
  // returns to the hub. Cross-section pivots (externalTo) navigate
  // to their own top-level route. Drill-out via the rail's "Explore
  // Home" item resets to the bare hub.
  const setActiveAndSync = (next: string | null) => {
    if (next) {
      const pivot = PIVOTS.find((p) => p.id === next);
      if (!pivot) return;
      if (pivot.externalTo) {
        const handlePrefix = location.pathname.startsWith("/u/")
          ? location.pathname.replace(/(\/u\/[^/]+).*/, "$1")
          : "";
        navigate(`${handlePrefix}${pivot.externalTo}`);
        return;
      }
      setActive(next);
      navigate(`${base}/${pivot.slug}`);
      void persistLastLens(next);
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        setActive(null);
        navigate(base, { replace: true });
      }
    }
  };

  const current = active ? PIVOTS.find((p) => p.id === active) ?? null : null;

  // The rail always shows a synthetic "Explore Home" item at the top
  // mapping to /explore (the hub tiles). It is the selected item when
  // no lens is active, so the rail + breadcrumb stay present on the
  // landing instead of appearing only after drilling into a child.
  const onRailPick = (id: string) => {
    if (id === "home") {
      setActive(null);
      navigate(base);
      return;
    }
    setActiveAndSync(id);
  };

  const railItems: ReadonlyArray<L2NavItem> = [
    { id: "home", label: "Explore Home", Icon: Compass },
    ...PIVOTS.map((p) => ({
      id: p.id,
      label: p.label,
      Icon: p.Icon,
      hint: p.hint,
    })),
  ];

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb
        segments={
          current
            ? [
                { label: "Explore", href: "/explore" },
                { label: current.label },
              ]
            : [{ label: "Explore" }]
        }
      />
      <header className="space-y-1 min-w-0">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate">
          {current ? current.label : "Explore"}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {current ? current.hint : "Lenses on the same Memso. Pick one or browse."}
        </p>
      </header>

      <L2RailShell
        items={railItems}
        activeId={active ?? "home"}
        onPick={onRailPick}
        railAriaLabel="Explore lens"
        listAriaLabel="Pick a lens"
        tint="violet"
        mobileHeader={<TeachingBanner id="explore_overview" />}
      >
        {!current && savedLensReady && (
          <div className="space-y-5">
            <TeachingBanner id="explore_overview" />
            <LensHub onPick={setActiveAndSync} />
          </div>
        )}
        {!savedLensReady && !current && <HubSkeleton />}

        {current && LENS_TEACHING_ID[current.id] && (
          <div className="mb-5">
            <TeachingBanner id={LENS_TEACHING_ID[current.id]} />
          </div>
        )}

        {current && current.id === "conversation" && <Conversation />}
        {current && current.id === "weave" && <Weave />}
        {current && current.id === "graph" && <KnowledgeGraph />}
        {current && current.id === "about-you" && <AboutYou />}
        {current && current.id === "people" && <People />}
        {current && current.id === "relationships" && (
          <Relationships onOpenPerson={() => setActiveAndSync("people")} />
        )}
        {current && current.id === "dates" && <Dates />}
      </L2RailShell>
    </div>
  );
}

function LensHub({ onPick }: { onPick: (id: string) => void }) {
  return (
    <ul
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      aria-label="Pick a lens"
    >
      {PIVOTS.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => onPick(p.id)}
            className="w-full h-full text-left glass rounded-2xl border backdrop-blur p-4 flex items-center gap-3 transition-colors border-slate-200/70 dark:border-slate-700/50 hover:border-violet-300 dark:hover:border-violet-600 hover:bg-white/70 dark:hover:bg-slate-800/60"
          >
            <span
              className={`flex-shrink-0 inline-flex w-12 h-12 rounded-2xl items-center justify-center text-white shadow-md bg-gradient-to-br ${tintGradient(p.tint)}`}
            >
              <p.Icon className="w-6 h-6" strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">
                  {p.label}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                {p.blurb}
              </p>
            </div>
            <ArrowRight className="flex-shrink-0 w-5 h-5 text-slate-400 dark:text-slate-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function HubSkeleton() {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <li
          key={i}
          className="h-20 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse"
        />
      ))}
    </ul>
  );
}

function tintGradient(tint: Pivot["tint"]): string {
  switch (tint) {
    case "violet":
      return "from-violet-400 to-fuchsia-500";
    case "amber":
      return "from-amber-400 to-orange-500";
    case "sky":
      return "from-sky-400 to-indigo-500";
    case "emerald":
      return "from-emerald-400 to-teal-500";
    case "pink":
      return "from-pink-400 to-rose-500";
    case "slate":
      return "from-slate-400 to-slate-600";
  }
}

